import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INVALID_CHARACTERS, removeSpecialCharacters } from '../save-asset-dialog/removeSpecialCharacters';
import { SaveAssetDialog, SaveAssetDialogContent } from '../save-asset-dialog/SaveAssetDialog';
import { SaveAssetDialogActions } from '../save-asset-dialog/SaveAssetDialogActions';
import { SaveAssetDialogNameInput } from '../save-asset-dialog/SaveAssetDialogNameInput';
import { SaveAssetDialogTitle } from '../save-asset-dialog/SaveAssetDialogTitle';
import { useSaveSnippetMutation } from './useSaveSnippetMutation';
import { FolderSelection } from '../common/FolderSelection';
import Divider from '@mui/material/Divider';

const MIN_NAME_LENGTH = 3;

const isValidName = (name: string) => {
  return name.length >= MIN_NAME_LENGTH && !INVALID_CHARACTERS.test(name);
};

type SaveAssetToLibraryDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  blockId: string;
};

export function SaveAssetToLibraryDialog({ isOpen, onClose, blockId }: SaveAssetToLibraryDialogProps) {
  const { t } = useTranslation();
  const [assetName, setAssetName] = useState('');
  const [hasError, setHasError] = useState(false);

  const [folderId, setFolderId] = useState('root');

  const handleClose = () => {
    setAssetName('');
    setHasError(false);
    setFolderId('');
    onClose();
  };

  const { saveSnippet, isLoading } = useSaveSnippetMutation({ onSuccess: handleClose, onError: handleClose });

  useEffect(() => {
    setHasError(assetName.length > 0 && !isValidName(assetName));
  }, [assetName]);

  const handleCreateSnippet = () => {
    const trimmedSnippetName = assetName.trim();

    setAssetName(trimmedSnippetName);

    if (!isValidName(trimmedSnippetName)) {
      setHasError(true);
      return;
    }

    saveSnippet({ name: trimmedSnippetName, blockId, folderId });
  };

  const handleFix = () => {
    const cleanedName = removeSpecialCharacters(assetName);
    setAssetName(cleanedName);
    setHasError(false);
  };

  return (
    <SaveAssetDialog isOpen={isOpen} onClose={handleClose} isLoading={isLoading}>
      <SaveAssetDialogTitle title={t('library.save_snippet_dialog_title')} onClose={handleClose} />
      <SaveAssetDialogContent>
        <SaveAssetDialogNameInput
          label={t('library.save_snippet_dialog_input_label')}
          assetName={assetName}
          onBlur={setAssetName}
          showFixButton={hasError && assetName.length >= MIN_NAME_LENGTH}
          onFix={handleFix}
          helperText={hasError ? t('library.save_snippet_naming_error_message', { minLength: MIN_NAME_LENGTH }) : undefined}
        />
        <Divider sx={{ my: 0.5, opacity: 0 }} />
        <FolderSelection assetName={assetName} onFolderSelected={setFolderId} assetType="SNIPPET" />
      </SaveAssetDialogContent>
      <SaveAssetDialogActions
        disabled={hasError || assetName.length === 0 || isLoading}
        onSave={handleCreateSnippet}
        onCancel={handleClose}
      />
    </SaveAssetDialog>
  );
}
